/**
 * Display formatting for values that arrive from the admin API.
 *
 * Everything here is one-way: API shape in, string for a human out. Nothing in
 * this file parses a formatted value back, and nothing does arithmetic.
 */

/** The one absent-value glyph. Not "N/A", not an empty cell, not "null". */
export const EM_DASH = "—";

/**
 * Formats a money amount for display.
 *
 * The API sends money as a decimal string (see amountString in
 * internal/api/handler/admin/money.go), and this function never turns it into a
 * number. That is the whole point: the string is exact on the wire, IEEE-754 is
 * not, and when the amount_minor cutover happens the server changes and this
 * does not.
 *
 * IDR has no minor unit in practice, so ".00" is dropped -- Rp 1.234.567, with
 * the thousands separator Indonesian formatting uses. A non-zero fraction is
 * NOT dropped: it is vanishingly rare in this data, and silently hiding part of
 * an amount is worse than an unfamiliar-looking cell.
 */
export function formatIDR(decimal: string): string {
  const trimmed = decimal.trim();
  if (trimmed === "") return EM_DASH;

  const negative = trimmed.startsWith("-");
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole = "0", fraction = ""] = unsigned.split(".");

  // Group from the right in threes. \B keeps it off the leading edge, so "123"
  // does not become ".123".
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const cents = /^0*$/.test(fraction) ? "" : `,${fraction}`;

  return `Rp ${negative ? "-" : ""}${grouped}${cents}`;
}

/** formatIDR for a column the API omits when it has no value. */
export function formatIDROptional(decimal: string | null | undefined): string {
  return decimal == null ? EM_DASH : formatIDR(decimal);
}

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const DATE_ONLY = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
});

/**
 * Renders an RFC 3339 timestamp in the reader's local time.
 *
 * A missing or unparseable value is an em dash rather than "Invalid Date".
 * expires_at is null for every v1-created transaction and everything predating
 * migration 010, so this path is the common case on that column, not an edge.
 */
export function formatDateTime(iso: string | null | undefined): string {
  const date = toDate(iso);
  return date ? DATE_TIME.format(date) : EM_DASH;
}

/** formatDateTime without the clock, for dates where the time is noise. */
export function formatDate(iso: string | null | undefined): string {
  const date = toDate(iso);
  return date ? DATE_ONLY.format(date) : EM_DASH;
}

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Any nullable string as a table cell: the value, or the em dash. */
export function orDash(value: string | null | undefined): string {
  return value == null || value === "" ? EM_DASH : value;
}

const NUMBER = new Intl.NumberFormat(undefined);

/** A count, grouped. Row totals and quantities, never money. */
export function formatCount(value: number): string {
  return NUMBER.format(value);
}

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

/** Thresholds for formatRelative, largest unit first. */
const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 60 * 60],
  ["month", 30 * 24 * 60 * 60],
  ["day", 24 * 60 * 60],
  ["hour", 60 * 60],
  ["minute", 60],
  ["second", 1],
];

/**
 * "3 minutes ago", "in 2 hours".
 *
 * The debugging screens are read in the present tense -- "did this arrive
 * today?", "is that retry overdue?" -- and an absolute timestamp makes the
 * reader do the subtraction. It is never shown alone: every use pairs it with
 * formatDateTime in a title or beside it, because the relative form loses the
 * precision you need the moment you start correlating with a provider's log.
 *
 * Absent or unparseable is an em dash, like the rest of this file.
 */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  const date = toDate(iso);
  if (!date) return EM_DASH;

  const seconds = (date.getTime() - now.getTime()) / 1000;
  const magnitude = Math.abs(seconds);

  // Below a minute the exact count is noise, and "in -3 seconds" from a clock
  // skew of a second or two is worse than noise.
  if (magnitude < 45) return "just now";

  for (const [unit, size] of RELATIVE_UNITS) {
    if (magnitude >= size) {
      return RELATIVE.format(Math.round(seconds / size), unit);
    }
  }
  return "just now";
}

/**
 * A duration in milliseconds, as a human reads it.
 *
 * Used for two things that look alike and are not: how long one HTTP attempt
 * took (callback_delivery_attempts.duration_ms, typically a few hundred ms) and
 * how long a gap in the timeline is (potentially hours). One function so a
 * 1,240ms attempt and a 4h 12m hole are formatted by the same rules.
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return EM_DASH;

  const total = Math.abs(Math.round(ms));
  if (total < 1000) return `${formatCount(total)}ms`;

  const seconds = Math.round(total / 1000);
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    const rest = seconds % 60;
    return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
  }

  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest === 0 ? `${days}d` : `${days}d ${rest}h`;
}
